import { useMutation, useQueryClient } from "@tanstack/react-query";
import { UserMinus, UserPlus } from "lucide-react";
import { Link } from "wouter";
import { gqlRequest } from "../lib/gql";
import { FOLLOW_PERSON, UNFOLLOW_PERSON } from "../lib/queries";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { Button } from "@/components/ui/button";

/**
 * Follow / unfollow toggle for one person.
 *
 * Anonymous visitors get a link to the login page rather than a disabled
 * button — browsing profiles is open to everyone, only the write is gated.
 * Renders nothing on your own profile: person_follow_not_self rejects a
 * self-follow at the database, so there is no button to offer.
 */
export function FollowButton({
  personId,
  isFollowing,
  onChange,
}: {
  personId: string;
  isFollowing: boolean;
  onChange?: () => void;
}) {
  const { personId: viewerId } = useAuth();
  const { t } = useT();
  const queryClient = useQueryClient();

  const toggle = useMutation({
    mutationFn: () =>
      gqlRequest(isFollowing ? UNFOLLOW_PERSON : FOLLOW_PERSON, {
        followerId: viewerId,
        followeeId: personId,
      }),
    onSuccess: () => {
      // Both profiles change: the target's follower list and the viewer's
      // following list.
      queryClient.invalidateQueries({ queryKey: ["person", personId] });
      queryClient.invalidateQueries({ queryKey: ["person", viewerId] });
      onChange?.();
    },
  });

  if (viewerId === personId) return null;

  if (!viewerId) {
    return (
      <Button variant="outline" asChild>
        <Link href={`/login?next=${encodeURIComponent(`/person/${personId}`)}`}>
          <UserPlus aria-hidden className="size-4" />
          {t("follow.follow")}
        </Link>
      </Button>
    );
  }

  return (
    <Button
      type="button"
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
      variant={isFollowing ? "outline" : "default"}
    >
      {isFollowing ? (
        <>
          <UserMinus aria-hidden className="size-4" />
          {t("follow.following")}
        </>
      ) : (
        <>
          <UserPlus aria-hidden className="size-4" />
          {t("follow.follow")}
        </>
      )}
    </Button>
  );
}
